import React from "react";
import { Row, Col } from "antd";

/**
 * Componente Card para mostrar información organizada con iconos
 *
 * @param {string}    title       - Título del card
 * @param {ReactNode} icon        - Icono principal (de @ant-design/icons)
 * @param {string}    iconColor   - Color del icono y acento (default: '#1890ff')
 * @param {string}    accentColor - Color del borde izquierdo + cabecera. Si se omite usa iconColor
 * @param {boolean}   compact     - Padding reducido (default: false)
 * @param {Array}     sections    - [{ title, icon, iconColor, items: [{ label, value }], content, span, lg }]
 * @param {number}    span        - Columnas del card (default: 24)
 * @param {Object}    splitWith   - Cuando se pasa, renderiza un segundo card a la derecha (cada uno ocupa 12/24).
 *                                  Acepta: { title, icon, iconColor, accentColor, compact, sections }
 */

/** header de una tarjeta  */
const CardHeader = ({ cardAccent, cardIcon, cardTitle, dense = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: dense ? "8px" : "10px",
      marginBottom: dense ? "8px" : "14px",
      paddingBottom: dense ? "6px" : "10px",
      borderBottom: `2px solid ${cardAccent}55`,
    }}
  >
    {cardIcon && (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: dense ? 26 : 32,
          height: dense ? 26 : 32,
          borderRadius: "8px",
          background: `${cardAccent}18`,
          color: cardAccent,
          fontSize: dense ? "14px" : "16px",
          flexShrink: 0,
        }}
      >
        {cardIcon}
      </span>
    )}
    <span
      style={{
        background: 'transparent',
        color: '#333',
        fontSize: '14px',
        fontWeight: 600, 
        letterSpacing: "0.3px",
      }}
    >
      {cardTitle}
    </span>
  </div>
);

/**cuerpo de secciones */
const CardSections = ({ sections, dense = false }) => (
  <Row gutter={dense ? [12, 8] : [12, 12]}>
    {sections.map((section, sectionIndex) => {
      const sectionAccent = section.iconColor || "#888";
      return (
        <Col
          key={sectionIndex}
          span={section.span || 24}
          xs={section.xs || 24}
          sm={section.sm || 24}
          lg={section.lg}
        >
          {section.title && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: sectionAccent,
                marginBottom: dense ? "4px" : "8px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}
            >
              {section.icon && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: `${sectionAccent}18`,
                    color: sectionAccent,
                    fontSize: "11px",
                    flexShrink: 0,
                  }}
                >
                  {section.icon}
                </span>
              )}
              {section.title}
            </div>
          )}

          <div>
            {section.items?.map((item, itemIndex) => {
              if (!item.value && item.value !== 0) return null;

              if (item.inline !== false) {
                return (
                  <div
                    key={itemIndex}
                    style={{
                      fontSize: item.fontSize || (dense ? "13px" : "14px"),
                      color: item.color || "#555",
                      marginBottom: item.marginBottom || (dense ? "2px" : "5px"),
                      lineHeight: item.lineHeight || (dense ? "1.35" : "1.5"),
                    }}
                  >
                    {item.label && (
                      <span
                        style={{
                          fontWeight: 600,
                          color: item.labelColor || "#333",
                        }}
                      >
                        {item.label}:{" "}
                      </span>
                    )}
                    <span style={{ fontWeight: item.bold ? 700 : 400 }}>{item.value}</span>
                  </div>
                );
              }

              return (
                <div key={itemIndex} style={{ marginBottom: "8px" }}>
                  {item.label && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#999",
                        marginBottom: "2px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      {item.label}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: item.fontSize || "13px",
                      color: item.color || "#333",
                      lineHeight: item.lineHeight || "1.6",
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>

          {section.content && <div>{section.content}</div>}
        </Col>
      );
    })}
  </Row>
);

const InfoCard = ({
  title,
  icon,
  iconColor = "#1890ff",
  accentColor,
  compact = false,
  dense = false,
  sections = [],
  span = 24,
  xs = 24,
  sm = 24,
  lg,
  xl,
  splitWith, //config de la segunda tarjeta
  onClick,
  onDoubleClick,
  selected = false, // resalta el borde con accent -- para tarjetas clicables tipo filtro
  className,
}) => {
  const accent = accentColor || iconColor;
  const pad = compact ? "14px" : "20px";

  const cardStyle = (pad) => ({
    padding: pad,
    background: "white",
    borderRadius: "8px",
    boxShadow: selected ? `0 0 0 2px ${accent}` : "0 2px 10px rgba(0, 0, 0, 0.08)",
    border: selected ? `1px solid ${accent}` : "1px solid #f0f0f0",
    height: "100%",
  });


  if (splitWith) {
    const splitAccent = splitWith.accentColor || splitWith.iconColor || "#1890ff";
    const splitPad = (splitWith.compact ?? compact) ? "14px" : "20px";
    const splitDense = splitWith.dense ?? dense;

    return (
      <Col span={span} xs={xs} sm={sm} lg={lg} xl={xl}>
        <Row gutter={[16, 16]}>
          {/* Tarjeta izquierda (principal) */}
          <Col span={12} xs={24} sm={24} lg={12}>
            <div style={cardStyle(pad)}>
              <CardHeader cardAccent={accent} cardIcon={icon} cardTitle={title} dense={dense} />
              <CardSections sections={sections} dense={dense} />
            </div>
          </Col>

          {/* Tarjeta derecha (splitWith) */}
          <Col span={12} xs={24} sm={24} lg={12}>
            <div style={cardStyle(splitPad)}>
              <CardHeader
                cardAccent={splitAccent}
                cardIcon={splitWith.icon}
                cardTitle={splitWith.title}
                dense={splitDense}
              />
              <CardSections sections={splitWith.sections || []} dense={splitDense} />
            </div>
          </Col>
        </Row>
      </Col>
    );
  }

  /* ── Modo normal ── */
  return (
    <Col span={span} xs={xs} sm={sm} lg={lg} xl={xl}>
      <div
        className={className}
        style={{ ...cardStyle(pad), cursor: onClick || onDoubleClick ? "pointer" : undefined }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        {/* ── Header ── */}
        <CardHeader cardAccent={accent} cardIcon={icon} cardTitle={title} dense={dense} />

        {/* ── Secciones ── */}
        <CardSections sections={sections} dense={dense} />
      </div>
    </Col>
  );
};

export default InfoCard;